import React, { PureComponent, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import FormikProvider from '../../shared/forms/formikProvider.component';
import Modal from '../../shared/modals/modal.component';
import Form from '../../shared/forms/form.component';

import { AdminTaxEntryPropTypes } from './adminTaxEntry.propTypes';
import schema from './taxEntryForm.schema';
import {
  typeDescriptionGetter,
  activityDescriptionGetter,
  slAccountGetter,
} from './valueGetters';

import {
  fetchInitialDataForTaxEntryModal,
} from './store/actions';

import {
  typeOptionsSelector,
  activityOptionsSelector,
  allowTopsideOptionsSelector,
  slViewOptionsSelector,
  allowEntryOptionsSelector,
  allowTransferOptionsSelector,

  fetchingDataForModalSelector,
  sharedSelectors,
} from './store/selectors';

const ModalTitles = {
  ADD: 'Add Tax Entry',
  EDIT: 'Edit Tax Entry',
};

class AddEditAdminTaxEntryModal extends PureComponent {
  static propTypes = {
    taxEntries: AdminTaxEntryPropTypes.isRequired,
    openForEdit: PropTypes.bool,
    entityToEdit: PropTypes.shape({
      periodName: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      typeDesc: PropTypes.string.isRequired,
      activity: PropTypes.string.isRequired,
      activityDesc: PropTypes.string.isRequired,
      slAccount: PropTypes.string.isRequired,
      allowTopside: PropTypes.string.isRequired,
      slView: PropTypes.string.isRequired,
      allowEntry: PropTypes.string.isRequired,
      allowTransfer: PropTypes.string.isRequired,
    }),
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    typeOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      desc: PropTypes.string.isRequired,
    })).isRequired,
    activityOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      desc: PropTypes.string.isRequired,
    })).isRequired,
    slViewOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    allowTopsideOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    allowEntryOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,
    allowTransferOptions: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })).isRequired,

    fetchingDataForModal: PropTypes.bool.isRequired,
    isSavingInProgress: PropTypes.bool.isRequired,

    hideModal: PropTypes.func.isRequired,

    fetchInitialData: PropTypes.func.isRequired,
    saveEntity: PropTypes.func.isRequired,
    isVisible: PropTypes.bool,
  };

  componentDidMount() {
    const { fetchInitialData } = this.props;

    fetchInitialData();
  }

  saveTaxEntry = (taxEntryData, { setSubmitting, resetForm }) => {
    const {
      period,
      saveEntity,
      openForEdit,
      typeOptions,
      activityOptions,
      slViewOptions,
      allowTopsideOptions,
      allowEntryOptions,
      allowTransferOptions,
    } = this.props;

    const valueGettersParams = {
      formik: { values: taxEntryData },
      formContext: {
        typeOptions,
        activityOptions,
        slViewOptions,
        allowTopsideOptions,
        allowEntryOptions,
        allowTransferOptions,
      },
    };

    const payload = ({
      ...taxEntryData,
      periodName: period.name,
      typeDesc: typeDescriptionGetter(valueGettersParams),
      activityDesc: activityDescriptionGetter(valueGettersParams),
      slAccount: slAccountGetter(valueGettersParams),
    });

    saveEntity(payload, openForEdit, { params: { periodName: period.name }, callback: resetForm });
    setSubmitting(false);
  };

  renderModal = ({ formik, schema }) => {
    const {
      openForEdit,
      typeOptions,
      activityOptions,
      slViewOptions,
      allowTopsideOptions,
      allowEntryOptions,
      allowTransferOptions,
      fetchingDataForModal,
      hideModal,
      isVisible,
      isSavingInProgress,
    } = this.props;

    const reset = useCallback(() => {
      hideModal();
      formik.resetForm();
    }, [formik, hideModal]);

    return (
      <Modal
        className="add-edit-admin-tax-entry-modal"
        title={openForEdit ? ModalTitles.EDIT : ModalTitles.ADD}
        closeAction={reset}
        dismissText="Cancel"
        dismissAction={reset}
        submitText="Save"
        submitAction={formik.handleSubmit}
        isLoading={fetchingDataForModal}
        isSubmitting={formik.isSubmitting || isSavingInProgress}
        submitDisabled={fetchingDataForModal}
        isVisible={isVisible}
      >
        <Form
          formik={formik}
          schema={schema}
          openForEdit={openForEdit}
          formContext={{
            typeOptions,
            activityOptions,
            slViewOptions,
            allowTopsideOptions,
            allowEntryOptions,
            allowTransferOptions,
          }}
        />
      </Modal>
    );
  };

  render() {
    const { entityToEdit, taxEntries, openForEdit } = this.props;

    return (
      <FormikProvider
        schema={schema}
        entityToEdit={entityToEdit}
        onSubmit={this.saveTaxEntry}
        render={this.renderModal}
        validationContext={{ entities: taxEntries, openForEdit }}
      />
    );
  }
}

const mapStateToProps = state => ({
  typeOptions: typeOptionsSelector(state),
  activityOptions: activityOptionsSelector(state),
  allowTopsideOptions: allowTopsideOptionsSelector(state),
  slViewOptions: slViewOptionsSelector(state),
  allowEntryOptions: allowEntryOptionsSelector(state),
  allowTransferOptions: allowTransferOptionsSelector(state),

  taxEntries: sharedSelectors.entitiesSelector(state),
  fetchingDataForModal: fetchingDataForModalSelector(state),
  isSavingInProgress: sharedSelectors.savingEntitySelector(state),
});

export default connect(mapStateToProps, {
  fetchInitialData: fetchInitialDataForTaxEntryModal,
})(AddEditAdminTaxEntryModal);
